import { Box, LinearProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import { FormDialog } from '../form-dialog';
import { ConfirmDialog } from '../confirm-dialog';
import { MultiFilePicker } from '../multi-file-picker';
import { FileUploadModalProps, FileUploadResult } from './file-upload-modal-props';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import { TextField } from '../text-field';

// The nginx proxy limit is 70 MB on the wire. Since file content is base64-encoded in the JSON request body,
// the effective max raw file size is ~52 MB (70 × 3/4). 50 MB is used as a safe client-side limit.
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
// Files above this size cannot be previewed as PDF (conversion times out), so conversion is not offered at all.
const MAX_CONVERSION_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const PDF_MIME_TYPES = ['application/pdf'];
const PDF_ACCEPT_ATTRIBUTE = '.pdf';

const ALL_SUPPORTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/vnd.ms-outlook',
  'message/rfc822',
  'text/plain',
  'text/html',
  'image/tiff',
  'image/png',
  'image/jpeg',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/rtf',
];
const ALL_SUPPORTED_ACCEPT_ATTRIBUTE = '.pdf,.docx,.doc,.xls,.xlsx,.csv,.msg,.eml,.emlx,.mht,.txt,.htm,.html,.tif,.tiff,.png,.jpg,.jpeg,.ppt,.pptx,.odt,.rtf';

export async function readFileAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      if (e.target?.result) {
        resolve(btoa(e.target.result));
      } else {
        reject(new Error('Error reading file'));
      }
    };
    reader.readAsBinaryString(file);
  });
}

function getFileNameWithoutExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
}

type ConversionPhase = 'selecting' | 'notifying' | 'confirming' | 'previewing' | 'uploading';

export function FileUploadModal(props: FileUploadModalProps) {
  const { open, onClose, onUploadComplete, convertToPdf, titlePrefix = 'Bilag - ', allowedFileTypes } = props;

  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [title, setTitle] = useState(titlePrefix);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadError, setUploadError] = useState('');
  const [partialResults, setPartialResults] = useState<FileUploadResult[]>([]);

  const [conversionPhase, setConversionPhase] = useState<ConversionPhase>('selecting');
  const [tooLargeForConversion, setTooLargeForConversion] = useState<File[]>([]);
  const [nonPdfQueue, setNonPdfQueue] = useState<File[]>([]);
  const [currentQueueFile, setCurrentQueueFile] = useState<File | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [isConvertingPreview, setIsConvertingPreview] = useState(false);
  const [conversionDecisions, setConversionDecisions] = useState<Map<string, boolean>>(new Map());

  const parsedAllowedExtensions =
    allowedFileTypes && allowedFileTypes.trim() !== '' && allowedFileTypes.trim().toLowerCase() !== 'allsupported'
      ? allowedFileTypes.split(';').map(fileType => fileType.trim()).filter(Boolean)
      : [];
  const hasAllowedFileTypes = parsedAllowedExtensions.length > 0;
  const dottedAllowedExtensions = hasAllowedFileTypes
    ? parsedAllowedExtensions.map(extension => `.${extension.replace(/^\./, '')}`)
    : undefined;
  const acceptedMimeTypes = convertToPdf ? ALL_SUPPORTED_MIME_TYPES : PDF_MIME_TYPES;
  const acceptAttribute = hasAllowedFileTypes
    ? dottedAllowedExtensions!.join(',')
    : convertToPdf
      ? ALL_SUPPORTED_ACCEPT_ATTRIBUTE
      : PDF_ACCEPT_ATTRIBUTE;
  const pdfMimeType = PDF_MIME_TYPES[0];

  function handleFilesChange(newFiles: File[]) {
    setFiles(newFiles);
    if (newFiles.length === 1) {
      setTitle(titlePrefix + getFileNameWithoutExtension(newFiles[0].name));
    }
  }

  function handleCancel() {
    const completed = partialResults;
    resetState();
    onClose();
    if (completed.length > 0) {
      onUploadComplete(completed);
    }
  }

  function resetState() {
    setFiles([]);
    setFileError('');
    setTitle(titlePrefix);
    setIsLoading(false);
    setUploadProgress({ current: 0, total: 0 });
    setUploadError('');
    setPartialResults([]);
    setConversionPhase('selecting');
    setTooLargeForConversion([]);
    setNonPdfQueue([]);
    setCurrentQueueFile(null);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl('');
    setIsConvertingPreview(false);
    setConversionDecisions(new Map());
  }

  async function handleConfirm() {
    if (files.length === 0) {
      setFileError('Vælg en eller flere filer at uploade');
      return;
    }

    if (!title.trim()) {
      setUploadError('Titel skal udfyldes');
      return;
    }

    if (convertToPdf) {
      const convertibleFiles = files.filter(f => f.type !== pdfMimeType && f.size <= MAX_CONVERSION_FILE_SIZE_BYTES);
      const oversizedNonPdfFiles = files.filter(f => f.type !== pdfMimeType && f.size > MAX_CONVERSION_FILE_SIZE_BYTES);
      const initialDecisions = new Map(oversizedNonPdfFiles.map(f => [f.name, false]));

      if (oversizedNonPdfFiles.length > 0) {
        setTooLargeForConversion(oversizedNonPdfFiles);
        setNonPdfQueue(convertibleFiles);
        setConversionDecisions(initialDecisions);
        setConversionPhase('notifying');
        return;
      }

      if (convertibleFiles.length > 0) {
        setNonPdfQueue(convertibleFiles);
        setCurrentQueueFile(convertibleFiles[0]);
        setConversionDecisions(initialDecisions);
        setConversionPhase('confirming');
        return;
      }

      await uploadFiles(initialDecisions);
      return;
    }

    await uploadFiles(new Map());
  }

  function handleTooLargeNotificationConfirm() {
    if (nonPdfQueue.length > 0) {
      setCurrentQueueFile(nonPdfQueue[0]);
      setConversionPhase('confirming');
    } else {
      setConversionPhase('uploading');
      uploadFiles(conversionDecisions);
    }
  }

  function handleConversionNo() {
    const decisions = new Map(conversionDecisions);
    decisions.set(currentQueueFile!.name, false);
    advanceQueue(decisions);
  }

  async function handleConversionYes() {
    setIsConvertingPreview(true);
    try {
      const client = moduleClientFactory.createCitizenCommunicationClient();
      const base64Content = await readFileAsBase64(currentQueueFile!);
      const response = await client.previewFileAttachmentAsPdf({
        fileName: currentQueueFile!.name,
        mimeType: currentQueueFile!.type,
        content: base64Content,
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(url);
      setConversionPhase('previewing');
    } catch {
      setUploadError('Forhåndsvisning af konverteret PDF fejlede');
      setConversionPhase('selecting');
    } finally {
      setIsConvertingPreview(false);
    }
  }

  function handlePreviewClose() {
    URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl('');
    setConversionPhase('selecting');
  }

  function handlePreviewApprove() {
    URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl('');
    const decisions = new Map(conversionDecisions);
    decisions.set(currentQueueFile!.name, true);
    advanceQueue(decisions);
  }

  function advanceQueue(decisions: Map<string, boolean>) {
    const currentIndex = nonPdfQueue.indexOf(currentQueueFile!);
    const nextFile = nonPdfQueue[currentIndex + 1];
    if (nextFile) {
      setConversionDecisions(decisions);
      setCurrentQueueFile(nextFile);
      setConversionPhase('confirming');
    } else {
      setConversionDecisions(decisions);
      setConversionPhase('uploading');
      uploadFiles(decisions);
    }
  }

  async function uploadFiles(decisions: Map<string, boolean>) {
    setIsLoading(true);
    setUploadError('');

    const client = moduleClientFactory.createCitizenCommunicationClient();
    const results: FileUploadResult[] = [];
    const failedFileNames: string[] = [];
    let errorMessage = '';

    const filesToUpload = files;
    setUploadProgress({ current: 0, total: filesToUpload.length });

    for (let i = 0; i < filesToUpload.length; i++) {
      setUploadProgress({ current: i + 1, total: filesToUpload.length });
      const file = filesToUpload[i];

      try {
        const base64Content = await readFileAsBase64(file);
        const fileTitle = files.length === 1 ? title : titlePrefix + getFileNameWithoutExtension(file.name);

        const response = await client.uploadFileAttachment({
          fileName: file.name,
          mimeType: file.type,
          content: base64Content,
          title: fileTitle,
          convertToPdf: decisions.get(file.name) === true,
        });

        results.push({
          documentId: response.documentId,
          title: response.title ?? '',
          fileName: response.fileName ?? '',
          mimeType: response.mimeType ?? '',
          fileSize: response.fileSize,
          blobReference: response.blobReference ?? '',
          owner: response.owner ?? '',
        });
      } catch (error: any) {
        failedFileNames.push(file.name);
        const data = error?.response?.data;
        if (data?.systemCode === 'FILE_TOO_LARGE') {
          errorMessage = `${data.title}: ${data.detail}`;
        } else if (error?.response?.status === 413) {
          errorMessage = `Filen er for stor til at uploade. Den maksimale filstørrelse er 50 MB.`;
        } else {
          errorMessage = `Upload fejlede for: ${file.name}`;
        }
      }
    }

    setIsLoading(false);
    setUploadProgress({ current: 0, total: 0 });

    if (failedFileNames.length > 0) {
      if (results.length > 0) {
        setPartialResults(results);
      }
      setUploadError(errorMessage);
      setConversionPhase('selecting');
      return;
    }

    resetState();
    onUploadComplete(results);
  }

  return (
    <>
      <FormDialog
        open={open && (conversionPhase === 'selecting' || conversionPhase === 'uploading')}
        title="Vedhæft fil"
        confirmButtonText="Vedhæft"
        cancelButtonText="Afbryd"
        onConfirmClick={handleConfirm}
        onCancelClick={handleCancel}
        onClose={handleCancel}
        isLoading={isLoading}
        maxWidth="md"
      >
        <Box sx={{ width: 500, maxWidth: '100%' }}>
          <Box sx={{ mb: 2 }}>
            <TextField label="Titel" value={title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)} variant="outlined" fullWidth required disabled={files.length > 1} />
          </Box>

          <MultiFilePicker
            files={files}
            onFilesChange={handleFilesChange}
            error={fileError}
            onErrorChange={setFileError}
            acceptedMimeTypes={acceptedMimeTypes}
            acceptedExtensions={dottedAllowedExtensions}
            acceptAttribute={acceptAttribute}
            maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
            disabled={isLoading}
            labels={{
              dropZoneHelperText: hasAllowedFileTypes
                ? 'Begrænsede filtyper'
                : convertToPdf
                  ? 'Alle understøttede filtyper'
                  : 'Kun PDF-filer',
              invalidFileTypeError: hasAllowedFileTypes || convertToPdf
                ? 'Filtypen er ikke understøttet'
                : 'Kun PDF-filer er tilladt',
            }}
          />

          {isLoading && uploadProgress.total > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Uploader {uploadProgress.current} af {uploadProgress.total}...
              </Typography>
              <LinearProgress variant="determinate" value={(uploadProgress.current / uploadProgress.total) * 100} sx={{ mt: 1 }} />
            </Box>
          )}

          {uploadError && <Box sx={{ color: 'error.main', fontSize: '0.875rem', mt: 1 }}>{uploadError}</Box>}
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={conversionPhase === 'notifying'}
        type="info"
        title="Filer kan ikke konverteres"
        message={`Følgende filer er over 25 MB og kan ikke konverteres til PDF. De vil blive vedhæftet i deres originale format:\n\n${tooLargeForConversion.map(f => f.name).join('\n')}`}
        confirmButtonText="Fortsæt"
        onConfirmClick={handleTooLargeNotificationConfirm}
        disableBackdropClick
      />

      <ConfirmDialog
        open={conversionPhase === 'confirming'}
        type="question"
        title="Konvertér til pdf-format"
        message={`Du har valgt filen "${currentQueueFile?.name}", der ikke er i pdf-format. Ønsker du at konvertere filen til pdf-format, inden den vedhæftes?\r\n\r\nBemærk, at hvis filen ikke konverteres til pdf, vil den ikke kunne indgå i en eventuel aktindsigtsrapport.`}
        confirmButtonText="Ja"
        cancelButtonText="Nej"
        onConfirmClick={handleConversionYes}
        onCancelClick={handleConversionNo}
        isLoading={isConvertingPreview}
        disableBackdropClick
      />

      <FormDialog
        open={conversionPhase === 'previewing'}
        title="Konvertér til pdf-format"
        confirmButtonText="Godkend"
        cancelButtonText="Luk vindue"
        onConfirmClick={handlePreviewApprove}
        onCancelClick={handlePreviewClose}
        onClose={handlePreviewClose}
        isLoading={isConvertingPreview}
        maxWidth="lg"
        fullWidth
        disableBackdropClick
      >
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Nedenfor vises den valgte fil konverteret til pdf-format. Du skal godkende, om konverteringen er korrekt.
          </Typography>
          {pdfPreviewUrl && <iframe src={pdfPreviewUrl} style={{ width: '100%', height: '70vh', border: 'none' }} title="PDF preview" />}
        </Box>
      </FormDialog>
    </>
  );
}
